import { useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { Building2, Check, Loader2, Plus, TriangleAlert, X } from 'lucide-react';

import CreateCompanyModal from '@/components/features/WorkOrders/forms/CreateCompanyModal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

export interface CompanySelectCompany {
    id: string;
    name: string;
    company_code: string;
    country: string;
}

type CompanySelectInput = Partial<CompanySelectCompany> & {
    uuid?: string;
};

interface RegistrySuggestion {
    id: string;
    code: string;
    name: string;
    address_label?: string | null;
}

export interface CompanySelectProps {
    value?: string;
    onValueChange: (value: string) => void;
    onCompanyResolved?: (company: CompanySelectCompany | null) => void;
    placeholder?: string;
    disabled?: boolean;
    allowCreate?: boolean;
    seedCompanies?: CompanySelectInput[];
    className?: string;
}

const normalizeCompany = (raw: CompanySelectInput): CompanySelectCompany => ({
    id: String(raw.id ?? raw.uuid ?? ''),
    name: String(raw.name ?? ''),
    company_code: String(raw.company_code ?? ''),
    country: String(raw.country ?? ''),
});

export function CompanySelect({
    value,
    onValueChange,
    onCompanyResolved,
    placeholder = 'Select a company...',
    disabled = false,
    allowCreate = true,
    seedCompanies = [],
    className,
}: CompanySelectProps) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [registryFailed, setRegistryFailed] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<CompanySelectCompany | null>(null);
    const [crmOptions, setCrmOptions] = useState<CompanySelectCompany[]>([]);
    const [registryOptions, setRegistryOptions] = useState<RegistrySuggestion[]>([]);

    const debouncedSearch = useDebounce(searchTerm, 300);

    const seedMap = useMemo(() => new Map(seedCompanies.map((c) => [String(c.id ?? c.uuid ?? ''), normalizeCompany(c)])), [seedCompanies]);

    // Resolve initial value → selected company
    useEffect(() => {
        if (!value) {
            setSelectedCompany(null);
            return;
        }

        const fromSeed = seedMap.get(value);
        if (fromSeed) {
            setSelectedCompany((prev) => (prev?.id === fromSeed.id ? prev : fromSeed));
            return;
        }

        const fromCrm = crmOptions.find((c) => c.id === value);
        if (fromCrm) {
            setSelectedCompany((prev) => (prev?.id === fromCrm.id ? prev : fromCrm));
            return;
        }

        let cancelled = false;
        void (async () => {
            try {
                const res = await axios.get('/api/companies', { params: { id: value, limit: 1 } });
                if (cancelled) return;
                const raw = res.data?.companies?.[0];
                if (!raw) return;
                const normalized = normalizeCompany(raw as CompanySelectInput);
                setSelectedCompany((prev) => (prev?.id === normalized.id ? prev : normalized));
            } catch {
                // silently ignore — company just won't pre-fill
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, seedMap]);

    // Search when popover opens or query changes
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setCrmOptions([]);
            setRegistryOptions([]);
            setRegistryFailed(false);
            return;
        }

        let cancelled = false;

        void (async () => {
            setIsSearching(true);
            setRegistryFailed(false);
            try {
                const query = debouncedSearch.trim();
                const doRegistry = query.length >= 2;

                const localRes = await axios.get('/api/companies', {
                    params: { search: query || undefined, limit: doRegistry ? 25 : 10, page: 1 },
                });
                if (cancelled) return;

                const localList: CompanySelectCompany[] = ((localRes.data?.companies ?? []) as CompanySelectInput[])
                    .map(normalizeCompany)
                    .filter((c) => c.id && c.name);

                setCrmOptions(localList);
                setIsSearching(false);

                if (!doRegistry) {
                    setRegistryOptions([]);
                    return;
                }

                try {
                    const regRes = await axios.get('/api/company/suggest', {
                        params: { q: query, limit: 15 },
                        timeout: 4000,
                    });
                    if (cancelled) return;

                    const localCodes = new Set(localList.map((c) => c.company_code).filter(Boolean));
                    const suggestions: RegistrySuggestion[] = (Array.isArray(regRes.data) ? regRes.data : []).filter(
                        (s: RegistrySuggestion) => s.code && s.name && !localCodes.has(s.code),
                    );
                    setRegistryOptions(suggestions);
                } catch {
                    if (!cancelled) setRegistryFailed(true);
                    setRegistryOptions([]);
                }
            } catch {
                if (!cancelled) {
                    setCrmOptions([]);
                    setRegistryOptions([]);
                }
                setIsSearching(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, debouncedSearch]);

    const confirmSelection = (company: CompanySelectCompany) => {
        setSelectedCompany(company);
        onValueChange(company.id);
        onCompanyResolved?.(company);
        setOpen(false);
    };

    const handleImport = async (suggestion: RegistrySuggestion) => {
        setIsImporting(true);
        try {
            const res = await axios.post('/api/company/import', { code: suggestion.code });
            const raw = res.data?.company;
            if (!raw) throw new Error('No company in response');
            const normalized = normalizeCompany(raw as CompanySelectInput);
            setCrmOptions((prev) => [normalized, ...prev.filter((c) => c.id !== normalized.id)]);
            confirmSelection(normalized);
        } catch {
            // Keep registry option visible; user can retry
        } finally {
            setIsImporting(false);
        }
    };

    const handleClear = () => {
        setSelectedCompany(null);
        onValueChange('');
        onCompanyResolved?.(null);
    };

    const handleCompanyCreated = (raw: CompanySelectInput) => {
        const normalized = normalizeCompany(raw);
        setCrmOptions((prev) => [normalized, ...prev.filter((c) => c.id !== normalized.id)]);
        confirmSelection(normalized);
        setIsCreateModalOpen(false);
    };

    return (
        <>
            {/* modal: keeps the list clickable when this sits inside a
                Dialog or Drawer, which makes the rest of the page inert. */}
            <Popover modal open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled || isImporting}
                        className={cn('w-full justify-between font-normal', !selectedCompany && 'text-muted-foreground', className)}
                    >
                        {isImporting ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Importing from registry…
                            </span>
                        ) : selectedCompany ? (
                            <span className="flex min-w-0 items-center gap-2">
                                <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="truncate">{selectedCompany.name}</span>
                                {selectedCompany.company_code && (
                                    <Badge variant="outline" className="shrink-0 text-xs">
                                        {selectedCompany.company_code}
                                    </Badge>
                                )}
                            </span>
                        ) : (
                            <span>{placeholder}</span>
                        )}
                        {selectedCompany && !disabled && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label="Clear selection"
                                className="text-muted-foreground hover:text-foreground ml-auto shrink-0 rounded p-0.5"
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleClear();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleClear();
                                    }
                                }}
                            >
                                <X className="h-3.5 w-3.5" />
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>

                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <Command shouldFilter={false}>
                        {/* No autoFocus: the list is already populated when the
                            popover opens, so raising the keyboard would cover it. */}
                        <CommandInput placeholder="Search by name or code…" value={searchTerm} onValueChange={setSearchTerm} />

                        <CommandList className="max-h-72">
                            {isSearching && (
                                <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Searching…
                                </div>
                            )}

                            {!isSearching && crmOptions.length === 0 && registryOptions.length === 0 && (
                                <CommandEmpty>{debouncedSearch.trim().length >= 2 ? 'No companies found.' : 'Type to search…'}</CommandEmpty>
                            )}

                            {!isSearching && crmOptions.length > 0 && (
                                <CommandGroup heading="In CRM">
                                    {crmOptions.map((company) => (
                                        <CommandItem
                                            key={company.id}
                                            value={`crm-${company.id}`}
                                            onSelect={() => confirmSelection(company)}
                                            className="cursor-pointer"
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4 shrink-0',
                                                    selectedCompany?.id === company.id ? 'opacity-100' : 'opacity-0',
                                                )}
                                            />
                                            <Building2 className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
                                            {/* The badges sit beside the name once there is room for
                                                both; below sm they wrap, so the name keeps the width
                                                it needs to stay distinguishable. */}
                                            <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                                                <span className="min-w-0 truncate">{company.name}</span>
                                                <div className="flex shrink-0 items-center gap-1">
                                                    {company.company_code && (
                                                        <Badge variant="outline" className="text-xs">
                                                            {company.company_code}
                                                        </Badge>
                                                    )}
                                                    <Badge variant="secondary" className="bg-success-subtle text-success-subtle-foreground text-xs">
                                                        CRM
                                                    </Badge>
                                                </div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}

                            {!isSearching && registryOptions.length > 0 && (
                                <>
                                    {crmOptions.length > 0 && <CommandSeparator />}
                                    <CommandGroup heading="From registry — not in CRM">
                                        {registryOptions.map((suggestion) => (
                                            <CommandItem
                                                key={suggestion.code}
                                                value={`reg-${suggestion.code}`}
                                                onSelect={() => {
                                                    void handleImport(suggestion);
                                                }}
                                                className="cursor-pointer"
                                                disabled={isImporting}
                                            >
                                                <Building2 className="text-muted-foreground mr-2 h-4 w-4 shrink-0 opacity-60" />
                                                <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                                                    <div className="min-w-0">
                                                        <span className="block truncate">{suggestion.name}</span>
                                                        {suggestion.address_label && (
                                                            <span className="text-muted-foreground block truncate text-xs">
                                                                {suggestion.address_label}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        <Badge variant="outline" className="text-xs">
                                                            {suggestion.code}
                                                        </Badge>
                                                        <Badge variant="outline" className="border-warning text-warning-subtle-foreground text-xs">
                                                            Not in CRM
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}

                            {!isSearching && registryFailed && debouncedSearch.trim().length >= 2 && (
                                <div className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-xs">
                                    <TriangleAlert className="text-warning-subtle-foreground h-3.5 w-3.5 shrink-0" />
                                    Registry unavailable — showing CRM results only.
                                </div>
                            )}
                        </CommandList>

                        {allowCreate && (
                            <>
                                <CommandSeparator />
                                <div className="p-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="w-full justify-start text-sm"
                                        onPointerDown={(e) => {
                                            e.preventDefault();
                                            setOpen(false);
                                            // Let the popover finish unmounting first: closing it
                                            // restores the body's pointer-events on a timer, which
                                            // would otherwise undo the modal's own lock.
                                            window.setTimeout(() => setIsCreateModalOpen(true), 0);
                                        }}
                                    >
                                        <Plus className="mr-2 h-4 w-4" />
                                        Create new company
                                    </Button>
                                </div>
                            </>
                        )}
                    </Command>
                </PopoverContent>
            </Popover>

            {allowCreate && (
                <CreateCompanyModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCompanyCreated} />
            )}
        </>
    );
}
